import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import axios from 'axios';
import { Check, ArrowLeft, Loader2, Crown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
  { id:'starter', name:'Starter', price:'$9.98', annual:'$7.98', desc:'For solo founders', color:'#00D4FF',
    features:['1 Brand','3 Channels','OmniView Dashboard','Basic Analytics','Email Support','Community Access'] },
  { id:'pro', name:'Pro', price:'$49', annual:'$39', desc:'For growing teams', color:'#C77DFF', popular:true,
    features:['5 Brands','15 Channels','OmniView + OmniThink','Advanced Analytics','Priority Support','API Access','Custom Reports'] },
  { id:'enterprise', name:'Enterprise', price:'$199', annual:'$159', desc:'For large agencies', color:'#FFD166',
    features:['Unlimited Brands','All Channels','Full Suite','AI Agents','Dedicated Manager','SLA 99.9%','White Label','Custom Integrations'] },
  { id:'bespoke', name:'Bespoke', price:'Custom', annual:'Custom', desc:'Done-With-You', color:'#06D6A0',
    features:['Everything in Enterprise','CRM Setup','Stock Tracking','Market Spying','Open Source Self-Hosted','Qwen 3.6 / Gemma 4','Lifetime Grandfather Rate'] },
];

export default function PricingPage() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [annual, setAnnual] = useState(false);
  const [loading, setLoading] = useState(null);

  const handleCheckout = async (planId) => {
    if (planId === 'bespoke') {
      window.open('mailto:hello@omniview.io?subject=Bespoke Plan Inquiry');
      return;
    }
    setLoading(planId);
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(`${API}/api/payments/checkout`,
        { plan_id: planId, origin_url: origin }, { withCredentials: true });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      alert(e.response?.data?.detail || 'Payment error');
    }
    setLoading(null);
  };

  return (
    <div className={`min-h-screen py-12 px-4 ${isDark ? 'bg-zinc-950' : 'bg-zinc-50'}`}
      style={{ fontFamily: "'Outfit', sans-serif" }}>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <button data-testid="pricing-back" onClick={() => navigate('/dashboard')}
            className={`flex items-center gap-2 text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
            <ArrowLeft size={16}/> Back to Dashboard
          </button>
        </div>

        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold mb-3">Upgrade Your Plan</h1>
          <p className={`text-lg ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
            Currently on <span className="font-bold" style={{ color: '#00D4FF' }}>{user?.plan || 'Trial'}</span> plan
          </p>
          <div className={`inline-flex items-center gap-3 p-1 rounded-lg mt-6 ${isDark ? 'bg-zinc-800' : 'bg-zinc-200'}`}>
            <button data-testid="pricing-monthly-toggle" onClick={() => setAnnual(false)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${!annual ? 'bg-white text-zinc-900 shadow' : (isDark ? 'text-zinc-400' : 'text-zinc-500')}`}
              style={!annual ? {} : { background: 'transparent' }}>Monthly</button>
            <button data-testid="pricing-annual-toggle" onClick={() => setAnnual(true)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${annual ? 'bg-white text-zinc-900 shadow' : (isDark ? 'text-zinc-400' : 'text-zinc-500')}`}
              style={annual ? {} : { background: 'transparent' }}>
              Annual <span className="text-[10px] ml-1" style={{ color: '#06D6A0' }}>Save 20%</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PLANS.map(plan => {
            const isCurrent = user?.plan === plan.id;
            return (
              <div key={plan.id} data-testid={`upgrade-plan-${plan.id}`}
                className={`relative rounded-xl border p-6 transition-all ${plan.popular ? 'ring-2' : ''} ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-white'}`}
                style={plan.popular ? { boxShadow: `0 0 30px ${plan.color}15` } : {}}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-[10px] font-bold text-white"
                    style={{ background: plan.color }}>MOST POPULAR</div>
                )}
                {isCurrent && (
                  <div className="absolute -top-3 right-4 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-500 text-white">
                    <Crown size={10}/> CURRENT
                  </div>
                )}
                <h3 className="text-lg font-bold mb-1">{plan.name}</h3>
                <p className={`text-sm mb-3 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>{plan.desc}</p>
                <div className="mb-5">
                  <span className="text-3xl font-bold">{annual ? plan.annual : plan.price}</span>
                  {plan.price !== 'Custom' && <span className={`text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>/mo</span>}
                </div>
                <ul className="space-y-2.5 mb-6">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check size={14} className="mt-0.5 flex-shrink-0" style={{ color: plan.color }}/>
                      <span className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>{f}</span>
                    </li>
                  ))}
                </ul>
                <button data-testid={`checkout-${plan.id}`}
                  onClick={() => !isCurrent && handleCheckout(plan.id)}
                  disabled={isCurrent || loading === plan.id}
                  className="w-full py-2.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  style={isCurrent ? { background: isDark ? '#27272a' : '#e4e4e7', color: isDark ? '#71717a' : '#a1a1aa' }
                    : plan.popular ? { background: plan.color, color: '#fff' }
                    : { background: 'transparent', border: `1px solid ${plan.color}`, color: plan.color }}>
                  {loading === plan.id && <Loader2 size={14} className="animate-spin"/>}
                  {isCurrent ? 'Current Plan' : plan.id === 'bespoke' ? 'Talk to Us' : 'Upgrade'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
